/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo, useEffect } from 'react';
import { useReportWebVitals } from 'next/web-vitals';
import rtlPlugin from 'stylis-plugin-rtl';
import { StyleSheetManager } from 'styled-components';
import { ModelManager } from '@adobe/aem-spa-page-model-manager';
import { injectAuthorGlobalCss } from '@marriott/shared/mi-helper-utils';

import {
  PageContext,
  LoadedCompContext,
  SessionUpdateContext,
  usePageStateStore,
  useSessionUpdateZustandStore,
} from '@marriott/mi-shop-components';
import { pageInitialData, setSubDirectoryPrefix } from '@marriott/mi-shop-components/utils';
import { defineComponentMapping } from '../import-components';
import operationSignatures from '../_constants/operationSignatures.json';
import hotelQuickViewCacheConfig from '../_constants/hotelQuickViewCacheConfig.json';
import 'react-loading-skeleton/dist/skeleton.css';
import './styles.css';
import { useClientEnvVarsStore, useScrollStore, usePersistentGlobalStore } from '@marriott/mi-store-utils';
import {
  CustomModelClient,
  respGridUtil,
  canUseDOM,
  populatePIdataLayer,
  UserSessionProvider,
  DynamicDCAPageProvider,
} from '@marriott/mi-headless-utils';
import { useDCAPageModelStore } from '@marriott/mi-store-utils';
import { version as globalStylesVersion } from '@marriott/global-styles/package.json';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';
declare global {
  interface Window {
    jQuery: unknown;
    $: any;
    deployedEnvType: string;
    impressionArr: string[];
    impressionTrack: (value: string) => Record<string, string>;
    dataLayer?: Record<string, unknown>;
  }
}

const modelClient = new CustomModelClient(process.env.NEXT_PUBLIC_AEM_HOST);

if (process.env.NODE_ENV !== 'test') {
  ModelManager.initializeAsync({
    modelClient,
  });
}

const App = function (props) {
  const { Component, pageProps } = props;
  // log.debug('[STEP 1] App component', Component, process.env);
  // log.debug('[STEP 1] App pageProps', pageProps);

  // log.debug('_app.tsx client side: ', props, pageProps);
  const rtlPluginTs = (...args: unknown[]) => {
    return rtlPlugin(args[0] as number, args[1] as string);
  };

  const {
    sessionData,
    SESSION_APP_GET_CALL_URL,
    SESSION_APP_POST_CALL_URL,
    SESSION_GET_CALL_URL_CLIENT,
    resolvedUrl,
    requestId,
    currentLocale,
    headersData,
    cookies,
    query,
    model,
    isAuthorMode,
    MI_DATALAYER_APP_URL,
    datalayerParams,
    datalayerProperties,
    NEXT_PUBLIC_PRE_PROCESSING_ADF_URL,
    interceptorResponse,
    NEXT_PUBLIC_PRE_PROCESSING_RELATIVE_ADF_URL,
    sessionFetchedData, // session data fetched from productspecific call please maintain the same name
    NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT,
    apolloEnvVars,
    GOOGLE_MAP_API_KEY,
    BAIDU_MAP_API_KEY,
    DEPLOYED_ENV_CONFIG,
    ENABLE_ADV_SEARCH_DATALAYER_CALL,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
    GROUPS_DEV_URL,
    GROUPS_TEST_URL,
    isDTT,
    disableDistanceFromLocation,
    NEXT_PUBLIC_PRE_PROCESSING_URL,
    CACHE_CLEAR_URL,
    cnEnvVars,
    isBulgariDomain,
    NEXT_GLOBAL_ASSET_PREFIX,
    NEXT_PUBLIC_BULGARI_URL,
    featureFlagLogo,
    featureFlagSOO,
    featureFlagAdfZustand,
    featureFlagSerpZustand,
    featureFlagSerpEnableGeographicSearch,
    findHotelsRemoteSPA,
    NEXT_PUBLIC_AEM_PATH,
    featureFlagEnableConcurFlow,
  } = pageProps;

  const preHeader: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-request-id': requestId,
    Cookie: `sessionID=${cookies?.sessionID};UserIdToken=${cookies?.UserIdToken ? cookies?.UserIdToken : ''}`,
    'X-Host': headersData ? headersData['x-host'] : '',
    'accept-language': headersData?.['accept-language'] || process.env['NEXT_PUBLIC_DEFAULT_LANG'] || '',
  };
  const allowedComponents = model?.cqItems?.root?.[':items']?.responsivegrid?.allowedComponents?.components;
  const setEnvVars = useClientEnvVarsStore(state => state.setEnvVars);
  const featureFlags = featureFlagLogo && featureFlagSOO ? Object.assign({}, featureFlagLogo, featureFlagSOO) : {};
  // adding featureFlagAdfZustand like this as if I add featureFlagAdfZustand in above line like featureFlagLogo && featureFlagSOO && featureFlagAdfZustand
  // then it might break for SERP where we dont pass featureFlagAdfZustand
  if (featureFlagAdfZustand) {
    Object.assign(featureFlags, featureFlagAdfZustand);
  }
  if (featureFlagSerpZustand) {
    Object.assign(featureFlags, featureFlagSerpZustand);
  }
  if (featureFlagSerpEnableGeographicSearch) {
    Object.assign(featureFlags, featureFlagSerpEnableGeographicSearch);
  }
  if (featureFlagEnableConcurFlow) {
    Object.assign(featureFlags, featureFlagEnableConcurFlow);
  }
  const envVars = { ...apolloEnvVars, ...cnEnvVars, featureFlags: { ...featureFlags } };
  envVars['SESSION_GET_CALL_URL_CLIENT'] = pageProps?.SESSION_GET_CALL_URL_CLIENT;
  envVars['NEXT_PUBLIC_SUBMIT_SEARCH_URL'] = pageProps?.NEXT_PUBLIC_SUBMIT_SEARCH_URL;
  envVars['NEXT_PUBLIC_AVAILABILITY_SEARCH_URL'] = pageProps?.NEXT_PUBLIC_AVAILABILITY_SEARCH_URL;
  envVars['REQUEST_ID'] = requestId;
  envVars['ACCEPT_LANGUAGE'] = currentLocale?.replace('_', '-');
  envVars['DAC_IMG_DOMAIN'] = pageProps?.DAC_IMG_DOMAIN;
  envVars['GOOGLE_MAP_API_KEY'] = pageProps?.GOOGLE_MAP_API_KEY;
  envVars['NEXT_PUBLIC_PREFIX'] = pageProps?.NEXT_PUBLIC_PREFIX;
  envVars['NEXT_PUBLIC_BULGARI_URL'] = pageProps?.NEXT_PUBLIC_BULGARI_URL;
  envVars['SESSION_APP_POST_CALL_URL'] = pageProps?.SESSION_APP_POST_CALL_URL;
  envVars['BAIDU_MAP_API_KEY'] = pageProps?.BAIDU_MAP_API_KEY;
  envVars['TARGET_SEGMENT_PROPERTY_TOKEN'] = pageProps?.TARGET_SEGMENT_PROPERTY_TOKEN;
  envVars['TARGET_SEGMENT_MBOX_NAME'] = pageProps?.TARGET_SEGMENT_MBOX_NAME;
  envVars['GET_DESTINATION_TYPE_FLAG'] = pageProps?.GET_DESTINATION_TYPE_FLAG;
  envVars['NEXT_PUBLIC_AEM_PATH'] = pageProps?.NEXT_PUBLIC_AEM_PATH;
  envVars['NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT'] = pageProps?.NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT;

  setEnvVars(envVars);

  useEffect(() => {
    if (pageProps?.isAuthorMode) {
      injectAuthorGlobalCss({
        isAuthorMode: true,
        remoteUrl: pageProps?.remoteUrl,
        globalStylesVersion,
        rtlEnable: pageProps?.rtlEnable,
        assetPrefix: pageProps?.NEXT_GLOBAL_ASSET_PREFIX,
      });
    }
  }, [pageProps?.isAuthorMode]);

  // Populate persistent global store with session data for NGSearchForm component
  const updateGlobalData = usePersistentGlobalStore((state: any) => state.updateGlobalData);
  useEffect(() => {
    const sessionDataToUse = sessionData || sessionFetchedData;
    if (sessionDataToUse) {
      updateGlobalData('sessionData', sessionDataToUse);
    }
  }, [sessionData, sessionFetchedData, updateGlobalData]);

  const [sessionDataState, setSessionDataState] = useState<any>(sessionData || sessionFetchedData);
  const [resultsRetried, setResultsRetry] = useState(false);

  //This code will populate the PI data related datalayer elements on clientside
  if (canUseDOM) populatePIdataLayer();

  let subDirectoryContextPath = '';

  if (model) {
    subDirectoryContextPath = model.subDirectoryContextPath ? model.subDirectoryContextPath : '';
    setSubDirectoryPrefix(subDirectoryContextPath);
  }

  defineComponentMapping(
    allowedComponents,
    resolvedUrl,
    isAuthorMode,
    currentLocale,
    isBulgariDomain,
    NEXT_PUBLIC_BULGARI_URL,
    requestId
  );
  const [pageState, setPageState] = useState<any>({ ...pageInitialData, authorMode: isAuthorMode });
  const [loadedCompState, setLaodedCompState] = useState<string[]>([]);

  const memoLoadedCompContextValue = useMemo(() => {
    const updateLoadedComponents = (componentName: string) => {
      if (!loadedCompState.includes(componentName)) {
        setLaodedCompState(preState => {
          return [...preState, componentName];
        });
      }
    };
    return { loadedCompState, updateLoadedComponents };
  }, [loadedCompState]);

  const memoPageContextValue = useMemo(() => {
    const host = headersData?.['x-host'];
    const updateComponentState = ({
      componentName,
      newState,
    }: {
      componentName: string;
      newState: Record<string, any>;
    }) => {
      setPageState(preState => {
        return { ...preState, [componentName]: newState };
      });
    };

    const updatePageErrors = ({ componentName, errors }) => {
      setPageState(preState => {
        return {
          ...preState,
          pageLevelErrors: {
            ...preState.pageLevelErrors,
            [componentName]: errors,
          },
        };
      });
    };

    return {
      pageVal: 'shop',
      featureFlagLogo,
      featureFlagSOO,
      featureFlagAdfZustand,
      featureFlagSerpZustand,
      featureFlagSerpEnableGeographicSearch,
      pageState,
      setPageState,
      updateComponentState,
      updatePageErrors,
      sessionData: sessionDataState,
      setSessionData: setSessionDataState,
      resolvedUrl,
      requestId,
      currentLocale: isAuthorMode ? 'en-US' : currentLocale?.replace('_', '-'),
      headersData,
      cookies,
      query,
      operationSignatures,
      hotelQuickViewCacheConfig,
      MI_DATALAYER_APP_URL,
      datalayerParams,
      datalayerProperties,
      NEXT_PUBLIC_PRE_PROCESSING_ADF_URL,
      NEXT_PUBLIC_PRE_PROCESSING_RELATIVE_ADF_URL,
      interceptorResponse,
      preHeader,
      SESSION_APP_GET_CALL_URL: `https://${host}${SESSION_APP_GET_CALL_URL}`,
      SESSION_APP_POST_CALL_URL: `https://${host}${SESSION_APP_POST_CALL_URL}`,
      SESSION_GET_CALL_URL_CLIENT: `https://${host}${SESSION_GET_CALL_URL_CLIENT}`,
      NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT,
      GOOGLE_MAP_API_KEY,
      BAIDU_MAP_API_KEY,
      DEPLOYED_ENV_CONFIG,
      ENABLE_ADV_SEARCH_DATALAYER_CALL,
      NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
      GROUPS_DEV_URL,
      GROUPS_TEST_URL,
      isDTT,
      disableDistanceFromLocation,
      NEXT_PUBLIC_PRE_PROCESSING_URL: `https://${host}${NEXT_PUBLIC_PRE_PROCESSING_URL}`,
      CACHE_CLEAR_URL: `https://${host}${CACHE_CLEAR_URL}`,
      isBulgariDomain,
      NEXT_PUBLIC_BULGARI_URL,
      isAuthorMode,
      findHotelsRemoteSPA,
      resultsRetried,
      setResultsRetry,
      NEXT_PUBLIC_AEM_PATH,
    };
  }, [
    featureFlagLogo,
    featureFlagSOO,
    featureFlagAdfZustand,
    featureFlagSerpZustand,
    featureFlagSerpEnableGeographicSearch,
    pageState,
    setPageState,
    sessionData,
    sessionDataState,
    setSessionDataState,
    resolvedUrl,
    requestId,
    currentLocale,
    headersData,
    cookies,
    query,
    operationSignatures,
    hotelQuickViewCacheConfig,
    MI_DATALAYER_APP_URL,
    datalayerParams,
    datalayerProperties,
    NEXT_PUBLIC_PRE_PROCESSING_ADF_URL,
    interceptorResponse,
    preHeader,
    sessionFetchedData,
    NEXT_PUBLIC_PRE_PROCESSING_RELATIVE_ADF_URL,
    SESSION_APP_GET_CALL_URL,
    SESSION_APP_POST_CALL_URL,
    SESSION_GET_CALL_URL_CLIENT,
    NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT,
    GOOGLE_MAP_API_KEY,
    BAIDU_MAP_API_KEY,
    DEPLOYED_ENV_CONFIG,
    ENABLE_ADV_SEARCH_DATALAYER_CALL,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
    GROUPS_DEV_URL,
    GROUPS_TEST_URL,
    isDTT,
    disableDistanceFromLocation,
    NEXT_PUBLIC_PRE_PROCESSING_URL,
    CACHE_CLEAR_URL,
    NEXT_PUBLIC_BULGARI_URL,
    SESSION_GET_CALL_URL_CLIENT,
    isBulgariDomain,
    findHotelsRemoteSPA,
    resultsRetried,
    setResultsRetry,
    NEXT_PUBLIC_AEM_PATH,
  ]);
  const pageStateStoreInitialization = usePageStateStore((state: any) => state.pageStateStoreInitialization);

  // Get SessionUpdate Zustand store for initialization
  const sessionUpdateStore = useSessionUpdateZustandStore();

  useEffect(() => {
    if (
      featureFlagAdfZustand?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND] ||
      featureFlagSerpZustand?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND]
    ) {
      pageStateStoreInitialization(memoPageContextValue);
    }
  }, [memoPageContextValue, featureFlagAdfZustand, featureFlagSerpZustand]);

  const [interactionsSelected, setInteractionsSelected] = useState<any>({
    availability: false,
    price: false,
    points: false,
    amenities: false,
    brands: false,
    distance: false,
    transportation: false,
    hotelType: false,
    events: false,
    activities: false,
    states: false,
    city: false,
    pagination: false,
    taxesAndFees: false,
    currencyConversion: false,
    sortBy: false,
    quickFilters: false,
    savedProperties: false,
    listOrMapButton: false,
  });

  // Initialize SessionUpdate Zustand store with interactionsSelected state
  useEffect(() => {
    const shouldUseZustand = featureFlagSerpZustand?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

    if (shouldUseZustand && interactionsSelected) {
      // Only initialize if the store is still empty (hasn't been initialized yet)
      const storeHasInteractions = Object.values(sessionUpdateStore.interactionsSelected).some(value => value === true);

      if (!storeHasInteractions) {
        sessionUpdateStore.setInteractionsSelected(interactionsSelected);
      }
    }
  }, [interactionsSelected, featureFlagSerpZustand, sessionUpdateStore]);

  const responsiveGrid = model?.cqItems?.root?.[':items']?.responsivegrid;
  const respGridModel = respGridUtil(responsiveGrid);
  const respGridModelClone = JSON.parse(JSON.stringify(respGridModel)); //To deep clone the model

  const setPageModel = useDCAPageModelStore(state => state.setPageModel);
  setPageModel(respGridModelClone);

  const initialize = useScrollStore(state => state.initialize);
  if (!!responsiveGrid && !isAuthorMode) {
    initialize(responsiveGrid);
  }

  // React Compiler: Enhanced performance monitoring for shop product
  useReportWebVitals(metric => {
    const compilerEnabled = process.env.REACT_COMPILER_ENABLED !== 'false';
    
    // Console logging for dev/test/uat/perf environments
    if (/dev|test|uat|perf/i.test(DEPLOYED_ENV_CONFIG)) {
      console.log('next/web-vitals - Shop Metric: %s', metric.name, {
        metric,
        compilerEnabled,
        product: 'shop'
      });
    }

    // Track React Compiler performance impact for key metrics
    if (metric.name === 'FCP' || metric.name === 'LCP' || metric.name === 'CLS') {
      if (typeof console !== 'undefined' && console.info) {
        console.info(`Shop React Compiler ${compilerEnabled ? 'ON' : 'OFF'} - ${metric.name}: ${metric.value}${metric.name === 'CLS' ? '' : 'ms'}`);
      }
    }
  });

  Object.defineProperty(rtlPluginTs, 'name', { value: 'rtlPluginMonorepo' });

  return (
    <UserSessionProvider pageProps={pageProps} includePiData={true}>
      <LoadedCompContext.Provider value={memoLoadedCompContextValue}>
        {/* PageContext.Provide to be removed once all the pages are migrated to use zustand */}
        <PageContext.Provider value={memoPageContextValue}>
          <SessionUpdateContext.Provider value={{ interactionsSelected, setInteractionsSelected }}>
            <StyleSheetManager stylisPlugins={pageProps?.model?.rtlEnable === 'true' ? [rtlPluginTs] : []}>
              <DynamicDCAPageProvider Component={Component} pageProps={pageProps} />
            </StyleSheetManager>
          </SessionUpdateContext.Provider>
        </PageContext.Provider>
      </LoadedCompContext.Provider>
    </UserSessionProvider>
  );
};

export default App;
